// src/utils/pricing.ts
//
// Výpočet startovného a cenové kategorie střelce pro závod.
// Přesunuto 1:1 z UserDashboard.calculatePrice (krok 4 redesignu) – výpočet
// se NESMÍ měnit. PaymentRace zatím drží vlastní (shodnou) kopii; na tento
// modul se sjednotí v kroku 6.

/** Minimální tvar závodu potřebný k výpočtu ceny. */
export interface PriceRace {
  /** Firestore Timestamp, řetězec "YYYY-MM-DD" nebo Date. */
  date: any;
  priceMember?: number;
  priceNonMember?: number;
  priceWoman?: number;
  priceJunior?: number;
}

/** Minimální tvar uživatele potřebný k výpočtu ceny. */
export interface PriceUser {
  birthYear: number;
  /** Pohlaví VELKÝMI písmeny ("ŽENA"/"MUŽ") – tak ho normalizují čtenáři users dokumentu. */
  gender: string;
  /** Členství podle roku; hodnota "ZAPLACENO" (bez ohledu na velikost písmen) = člen. */
  membership?: { [year: string]: string };
}

/** Cenová kategorie uživatele pro konkrétní závod. */
export type PriceCategory = "member" | "memberWoman" | "memberJunior" | "nonMember";

/**
 * Bezpečný převod Firestore Timestamp / string / Date → Date | null.
 * (Stejná implementace jako původní helper v UserDashboardu.)
 */
export const toDateSafe = (x: any): Date | null => {
  if (!x) return null;
  if (x?.seconds) return new Date(x.seconds * 1000); // Timestamp-like
  if (typeof x?.toDate === "function") return x.toDate(); // Firestore Timestamp
  const d = new Date(x);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Určí cenovou kategorii uživatele pro závod.
 *
 * Pravidla (beze změny vůči původnímu calculatePrice):
 * - členství se posuzuje podle ROKU ZÁVODU (membership[rok] === "ZAPLACENO"),
 * - žena má přednost před juniorem,
 * - junior jen když je načtený juniorMaxAge (defaults/categories) a
 *   věk v roce závodu (rok závodu − rok narození) je ≤ juniorMaxAge,
 * - neplatné datum závodu nebo nezaplacené členství → nečlen.
 *
 * @param juniorMaxAge Maximální věk juniora z defaults/categories; null = ještě nenačteno.
 */
export function getPriceCategory(
  race: PriceRace,
  user: PriceUser,
  juniorMaxAge: number | null
): PriceCategory {
  const raceDateObj = toDateSafe(race.date);
  if (!raceDateObj) return "nonMember";

  const raceYear = raceDateObj.getFullYear();
  const membershipForYear = user.membership
    ? user.membership[raceYear.toString()]
    : undefined;

  if (membershipForYear && membershipForYear.toUpperCase() === "ZAPLACENO") {
    if (user.gender === "ŽENA") return "memberWoman";
    const age = raceYear - user.birthYear;
    if (juniorMaxAge !== null && age <= juniorMaxAge) return "memberJunior";
    return "member";
  }
  return "nonMember";
}

/**
 * Startovné pro uživatele: cena podle kategorie z getPriceCategory,
 * chybějící ceny závodu se berou jako 0. Výpočet je 1:1 s původním
 * UserDashboard.calculatePrice – jen juniorMaxAge předává volající
 * (načítá se asynchronně z defaults/categories).
 */
export function calculatePrice(
  race: PriceRace,
  user: PriceUser,
  juniorMaxAge: number | null
): number {
  // Defaultní hodnoty cen
  const priceNonMember = race.priceNonMember ?? 0;
  const priceMember = race.priceMember ?? 0;
  const priceWoman = race.priceWoman ?? 0;
  const priceJunior = race.priceJunior ?? 0;

  switch (getPriceCategory(race, user, juniorMaxAge)) {
    case "memberWoman":
      return priceWoman;
    case "memberJunior":
      return priceJunior;
    case "member":
      return priceMember;
    default:
      return priceNonMember;
  }
}
